import { idadeEmMeses } from './derivacoes.js';

/**
 * Regras do cadastro de haras e animais. Puras: a tela usa para avisar na hora,
 * e o banco repete as que pode (0006_cadastro.sql) como ultima barreira.
 */

export interface AnimalCadastro {
  id: string;
  name: string;
  sex: 'macho_inteiro' | 'castrado' | 'femea' | '';
  birth_date: string;
  birth_date_estimated?: boolean;
  abccmm_registry: string | null;
  sire_id: string | null;
  dam_id: string | null;
}

export interface Problema {
  campo: string;
  mensagem: string;
}

export interface Validacao {
  erros: Problema[];
  avisos: Problema[];
}

const IDADE_MINIMA_PAI_MESES = 24;
const IDADE_MAXIMA_ANOS = 30;

const normalizarRegistro = (r: string | null) => (r ?? '').trim().toUpperCase().replace(/\s+/g, '');

/** O pai (ou a mae) e descendente do animal? Evita ciclo na genealogia. */
function descendeDe(candidato: string, ancestral: string, todos: ReadonlyMap<string, AnimalCadastro>): boolean {
  const vistos = new Set<string>();
  const fila = [candidato];
  while (fila.length) {
    const id = fila.pop()!;
    if (vistos.has(id)) continue;
    vistos.add(id);
    const a = todos.get(id);
    if (!a) continue;
    for (const pai of [a.sire_id, a.dam_id]) {
      if (!pai) continue;
      if (pai === ancestral) return true;
      fila.push(pai);
    }
  }
  return false;
}

export function validarAnimal(
  a: AnimalCadastro,
  { todos, hoje }: { todos: readonly AnimalCadastro[]; hoje: string },
): Validacao {
  const erros: Problema[] = [];
  const avisos: Problema[] = [];
  const porId = new Map(todos.map((x) => [x.id, x]));

  if (!a.name.trim()) erros.push({ campo: 'name', mensagem: 'Informe o nome do animal.' });
  if (!a.sex) erros.push({ campo: 'sex', mensagem: 'Informe o sexo.' });
  if (!a.birth_date) erros.push({ campo: 'birth_date', mensagem: 'Informe a data de nascimento (nunca a idade).' });
  else if (a.birth_date > hoje) erros.push({ campo: 'birth_date', mensagem: 'A data de nascimento está no futuro.' });
  else if (idadeEmMeses(a.birth_date, hoje) > IDADE_MAXIMA_ANOS * 12)
    avisos.push({ campo: 'birth_date', mensagem: `O animal teria mais de ${IDADE_MAXIMA_ANOS} anos. Confira a data.` });

  const registro = normalizarRegistro(a.abccmm_registry);
  if (registro) {
    const dono = todos.find((x) => x.id !== a.id && normalizarRegistro(x.abccmm_registry) === registro);
    if (dono) erros.push({ campo: 'abccmm_registry', mensagem: `Registro já usado por ${dono.name}.` });
  }

  const conferirPai = (campo: 'sire_id' | 'dam_id') => {
    const paiId = a[campo];
    if (!paiId) return;
    const papel = campo === 'sire_id' ? 'pai' : 'mãe';
    if (paiId === a.id) {
      erros.push({ campo, mensagem: `O animal não pode ser ${papel} de si mesmo.` });
      return;
    }
    const pai = porId.get(paiId);
    if (!pai) return;
    if (campo === 'sire_id' && pai.sex === 'femea') erros.push({ campo, mensagem: `${pai.name} é fêmea e não pode ser o pai.` });
    if (campo === 'dam_id' && pai.sex !== 'femea') erros.push({ campo, mensagem: `${pai.name} é macho e não pode ser a mãe.` });
    if (descendeDe(paiId, a.id, porId))
      erros.push({ campo, mensagem: `${pai.name} descende deste animal e não pode ser ${papel} dele.` });
    if (a.birth_date && pai.birth_date) {
      if (pai.birth_date >= a.birth_date && !pai.birth_date_estimated)
        erros.push({ campo, mensagem: `${pai.name} nasceu depois (ou no mesmo dia) deste animal.` });
      else if (idadeEmMeses(pai.birth_date, a.birth_date) < IDADE_MINIMA_PAI_MESES)
        avisos.push({
          campo,
          mensagem: `${pai.name} teria menos de ${IDADE_MINIMA_PAI_MESES} meses quando este animal nasceu.`,
        });
    }
  };
  conferirPai('sire_id');
  conferirPai('dam_id');

  return { erros, avisos };
}

export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
] as const;

export function validarHaras(h: { name: string; uf: string | null }): Validacao {
  const erros: Problema[] = [];
  if (!h.name.trim()) erros.push({ campo: 'name', mensagem: 'Informe o nome do haras.' });
  if (h.uf && !(UFS as readonly string[]).includes(h.uf)) erros.push({ campo: 'uf', mensagem: 'UF inválida.' });
  return { erros, avisos: [] };
}

/** Proximo numero de visita do haras. Numero apagado nao volta a ser usado. */
export function proximoNumeroDeVisita(visitas: readonly { visit_number: number }[]): number {
  return visitas.reduce((m, v) => Math.max(m, v.visit_number), 0) + 1;
}

/**
 * Data para quem so tem o ano de nascimento (pai ou mae de fora). Meio do ano:
 * o maior erro possivel fica em seis meses para qualquer lado. Sempre gravada
 * com birth_date_estimated = true.
 */
export function dataAproximada(ano: number): string {
  return `${String(ano).padStart(4, '0')}-07-01`;
}
